import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  LinearProgress,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  IconButton,
} from '@mui/material';
import { Pets, Assignment, TrendingUp, Warning, Close as CloseIcon } from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from 'recharts';
import { getCollection } from '../services/firestoreService';
import { useBranch } from '../context/BranchContext';

const getDateFromValue = (value) => {
  if (!value) return null;
  if (value.toDate) return value.toDate();
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return new Date(`${value}T00:00:00`);
  }
  return new Date(value);
};

const TopList = ({ title, data, total, color = 'primary' }) => {
  if (!data || !data.length) {
    return (
      <Paper sx={{ p: 2, height: '100%' }} variant="outlined">
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No hay datos disponibles.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2, height: '100%' }} variant="outlined">
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <Box>
        {data.map((item) => (
          <Box key={item.label} sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '70%',
                }}
              >
                {item.label}
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {item.count} ({total ? ((item.count / total) * 100).toFixed(1) : 0}%)
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={total ? (item.count / total) * 100 : 0}
              color={color}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

const IndicatorCard = ({ title, value, subtitle, icon, color = 'primary', onClick }) => (
  <Paper
    onClick={onClick}
    sx={{
      p: 2,
      display: 'flex',
      alignItems: 'flex-start',
      gap: 2,
      height: '100%',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'box-shadow 0.2s',
      '&:hover': onClick ? { boxShadow: 3 } : undefined,
    }}
    variant="outlined"
  >
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: '50%',
        bgcolor: `${color}.light`,
        color: `${color}.dark`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography variant="subtitle2" color="text.secondary">
        {title}
      </Typography>
      <Typography variant="h5">{value}</Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  </Paper>
);

const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const getYearMonth = (d) => {
  if (!d) return null;
  const date = d instanceof Date ? d : getDateFromValue(d);
  return date ? { year: date.getFullYear(), month: date.getMonth() + 1 } : null;
};

const ChangeLabel = ({ x, y, payload }) => {
  if (!payload) return null;
  const { current, previous } = payload;
  if (!current && !previous) return null;

  let text;
  let color;
  if (!previous) {
    if (current > 0) {
      text = '↑ +100%';
      color = '#2e7d32';
    } else {
      text = '0%';
      color = '#757575';
    }
  } else {
    const pct = ((current - previous) / previous) * 100;
    const sign = pct >= 0 ? '+' : '';
    text = `${pct >= 0 ? '↑' : '↓'} ${sign}${pct.toFixed(0)}%`;
    color = pct >= 0 ? '#2e7d32' : '#d32f2f';
  }

  return (
    <text x={x} y={y - 10} textAnchor="middle" fill={color} fontSize={12} fontWeight={600}>
      {text}
    </text>
  );
};

export default function SurgeryDashboardTab() {
  const now = new Date();
  const { activeBranchId, activeBranch, isAllBranches, filterByBranch } = useBranch();
  const [protocols, setProtocols] = useState([]);
  const [surgeries, setSurgeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [dialogType, setDialogType] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const [protocolsData, surgeriesData] = await Promise.all([
          getCollection('surgeryProtocols'),
          getCollection('surgeries'),
        ]);
        setProtocols(protocolsData || []);
        setSurgeries(surgeriesData || []);
      } catch (err) {
        console.error('Error cargando datos quirúrgicos:', err);
        setError('Error al cargar los protocolos quirurgicos.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const surgeriesMap = useMemo(() => {
    const map = new Map();
    surgeries.forEach((s) => { if (s.id) map.set(s.id, s); });
    return map;
  }, [surgeries]);

  const protocolBranchIds = useCallback((p) => {
    if (p?.branchId) return [p.branchId];
    if (Array.isArray(p?.branchIds) && p.branchIds.length) return p.branchIds;
    const s = p?.surgeryId && surgeriesMap.get(p.surgeryId);
    if (s?.branchId) return [s.branchId];
    if (Array.isArray(s?.branchIds) && s.branchIds.length) return s.branchIds;
    return [];
  }, [surgeriesMap]);

  const branchFiltered = useMemo(() => {
    if (isAllBranches) return protocols;
    return protocols.filter((p) => {
      const ids = protocolBranchIds(p);
      if (ids.length === 0) return true; // protocolos sin sucursal se muestran en la activa
      return ids.includes(activeBranchId);
    });
  }, [protocols, protocolBranchIds, activeBranchId, isAllBranches]);

  const branchFilteredSurgeries = useMemo(() => {
    return filterByBranch(surgeries || [], { includeUnassigned: true });
  }, [surgeries, filterByBranch]);

  const protocolsBySurgeryId = useMemo(() => {
    const map = new Map();
    branchFiltered.forEach((p) => { if (p.surgeryId) map.set(p.surgeryId, p); });
    return map;
  }, [branchFiltered]);

  const availableYears = useMemo(() => {
    const years = new Set([new Date().getFullYear()]);
    branchFiltered.forEach((p) => {
      const ymd = getYearMonth(p.date);
      if (ymd) years.add(ymd.year);
    });
    return Array.from(years).sort((a, b) => a - b);
  }, [branchFiltered]);

  const filtered = useMemo(() => {
    return branchFiltered.filter((p) => {
      const ymd = getYearMonth(p.date);
      return ymd && ymd.year === year && ymd.month === month;
    });
  }, [branchFiltered, year, month]);

  const chartData = useMemo(() => {
    return MESES.map((name, idx) => {
      const monthNumber = idx + 1;
      const current = branchFiltered.filter((p) => {
        const ymd = getYearMonth(p.date);
        return ymd && ymd.year === year && ymd.month === monthNumber;
      }).length;
      const previous = branchFiltered.filter((p) => {
        const ymd = getYearMonth(p.date);
        return ymd && ymd.year === year - 1 && ymd.month === monthNumber;
      }).length;
      return { month: name, current, previous };
    });
  }, [branchFiltered, year]);

  const stats = useMemo(() => {
    const total = filtered.length;
    if (!total) return null;

    const withField = (getter) => filtered.filter((p) => String(getter(p) || '').trim() !== '').length;

    const complicationCount = withField((p) => (p.surgeonProtocol || {}).complications);
    const findingsCount = withField((p) => (p.surgeonProtocol || {}).findings);
    const preOpDiagCount = withField((p) => (p.surgeonProtocol || {}).preOpDiagnosis);
    const postOpPlanCount = withField((p) => (p.surgeonProtocol || {}).postOpPlan);
    const descriptionCount = withField((p) => (p.surgeonProtocol || {}).description);

    const asaCount = withField((p) => (p.anesthesiaProtocol || {}).asaStatus);
    const fastingCount = withField((p) => (p.anesthesiaProtocol || {}).fastingHours);
    const planCount = withField((p) => (p.anesthesiaProtocol || {}).plan);
    const recoveryCount = withField((p) => (p.anesthesiaProtocol || {}).recovery);

    const topGroups = (getter, topN = 5) => {
      const counts = {};
      filtered.forEach((p) => {
        const raw = getter(p);
        const key = String(raw || 'Sin dato').trim();
        counts[key] = (counts[key] || 0) + 1;
      });
      return Object.entries(counts)
        .map(([label, count]) => ({ label, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, topN);
    };

    const procedureTypes = topGroups((p) => p.procedureType);
    const preOpDiagnoses = topGroups((p) => (p.surgeonProtocol || {}).preOpDiagnosis);
    const species = topGroups((p) => p.patientSpecies || 'Sin especie');
    const breeds = topGroups((p) => p.patientBreed || 'Sin raza', 8);
    const surgeons = topGroups((p) => p.mainSurgeon || 'Sin cirujano', 5);

    const hasAnesthesiaProtocol = (p) => {
      const ap = (p?.anesthesiaProtocol) || {};
      return String(ap.plan || '').trim() !== '' || String(ap.asaStatus || '').trim() !== '';
    };
    const hasSurgeonProtocol = (p) => {
      const sp = (p?.surgeonProtocol) || {};
      return (
        String(sp.description || '').trim() !== '' ||
        String(sp.preOpDiagnosis || '').trim() !== '' ||
        String(sp.findings || '').trim() !== ''
      );
    };

    const totalAnesthesia = filtered.filter(hasAnesthesiaProtocol).length;
    const anesthesiaByDoctor = Object.entries(
      filtered.reduce((acc, p) => {
        if (!hasAnesthesiaProtocol(p)) return acc;
        const name = String(p.anesthesiologist || p.anesthesiaProtocol?.anesthesiologist || 'Sin médico').trim();
        acc[name] = (acc[name] || 0) + 1;
        return acc;
      }, {})
    )
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count);

    const fullyDocumented = filtered.filter((p) => {
      const sp = p.surgeonProtocol || {};
      const ap = p.anesthesiaProtocol || {};
      return (
        String(sp.preOpDiagnosis || '').trim() !== '' &&
        String(sp.description || '').trim() !== '' &&
        String(sp.findings || '').trim() !== '' &&
        String(sp.postOpPlan || '').trim() !== '' &&
        String(ap.plan || '').trim() !== '' &&
        String(ap.recovery || '').trim() !== ''
      );
    }).length;

    // Cirugías completadas vs protocolos ingresados
    const completedSurgeries = branchFilteredSurgeries.filter((s) => {
      const d = getDateFromValue(s.date);
      if (!d) return false;
      const status = (s.status || '').toLowerCase();
      return (
        d.getFullYear() === year &&
        d.getMonth() + 1 === month &&
        (status === 'completada' || status === 'completed')
      );
    });
    const completedTotal = completedSurgeries.length;
    const completedWithSurgeon = completedSurgeries.filter((s) =>
      hasSurgeonProtocol(protocolsBySurgeryId.get(s.id))
    ).length;
    const completedWithAnesthesia = completedSurgeries.filter((s) =>
      hasAnesthesiaProtocol(protocolsBySurgeryId.get(s.id))
    ).length;
    const completedSurgeonMissing = completedTotal - completedWithSurgeon;
    const completedAnesthesiaMissing = completedTotal - completedWithAnesthesia;

    const surgeonMissing = {};
    const anestMissing = {};
    completedSurgeries.forEach((s) => {
      const sp = protocolsBySurgeryId.get(s.id);
      if (!hasSurgeonProtocol(sp) && String(s.mainSurgeon || '').trim() !== '') {
        surgeonMissing[s.mainSurgeon] = (surgeonMissing[s.mainSurgeon] || 0) + 1;
      }
      if (!hasAnesthesiaProtocol(sp) && String(s.anesthesiologist || '').trim() !== '') {
        anestMissing[s.anesthesiologist] = (anestMissing[s.anesthesiologist] || 0) + 1;
      }
    });

    const surgeonsMissing = Object.entries(surgeonMissing)
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count);
    const anestesiologistsMissing = Object.entries(anestMissing)
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count);

    return {
      total,
      complicationCount,
      findingsCount,
      preOpDiagCount,
      postOpPlanCount,
      descriptionCount,
      asaCount,
      fastingCount,
      planCount,
      recoveryCount,
      fullyDocumented,
      complicationRate: ((complicationCount / total) * 100).toFixed(1),
      documentationRate: ((fullyDocumented / total) * 100).toFixed(1),
      asaRate: ((asaCount / total) * 100).toFixed(1),
      checklistRate: (
        ((preOpDiagCount + descriptionCount + findingsCount + postOpPlanCount) / (total * 4)) *
        100
      ).toFixed(1),
      procedureTypes,
      preOpDiagnoses,
      species,
      breeds,
      surgeons,
      totalAnesthesia,
      anesthesiaByDoctor,
      completedTotal,
      completedWithSurgeon,
      completedWithAnesthesia,
      completedSurgeonMissing,
      completedAnesthesiaMissing,
      completedSurgeries,
      surgeonsMissing,
      anestesiologistsMissing,
    };
  }, [filtered, branchFilteredSurgeries, year, month, protocolsBySurgeryId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!stats) {
    return (
      <Paper sx={{ p: 3, mt: 2 }} variant="outlined">
        <Typography variant="h6" gutterBottom>
          Cirugías
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No hay cirugías registradas en {MESES[month - 1]} {year}.
        </Typography>
      </Paper>
    );
  }

  const branchLabel = isAllBranches
    ? 'Todas las sucursales'
    : `Sucursal: ${activeBranch?.name || activeBranchId}`;

  return (
    <Box sx={{ mt: 2 }}>
      <Paper sx={{ p: 2, mb: 3 }} variant="outlined">
        <Typography variant="h6" gutterBottom>
          Indicadores de gestión quirúrgica
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {branchLabel}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Basados en estandares de calidad y seguridad: tasa de complicaciones, completitud del
          protocolo de anestesia, registro de hallazgos y frecuencia de procedimientos. Estos
          indicadores reflejan las mejores practicas de control de calidad en cirugia veterinaria.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <TextField select size="small" label="Año" value={year} onChange={(e) => setYear(Number(e.target.value))} sx={{ minWidth: 100 }}>
            {availableYears.map((y) => (
              <MenuItem key={y} value={y}>{y}</MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label="Mes" value={month} onChange={(e) => setMonth(Number(e.target.value))} sx={{ minWidth: 120 }}>
            {MESES.map((m, i) => (
              <MenuItem key={m} value={i + 1}>{m}</MenuItem>
            ))}
          </TextField>
        </Box>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <IndicatorCard
              title="Cirugías"
              value={stats.total}
              subtitle={`Cirugías registradas en ${MESES[month - 1]} ${year}`}
              icon={<Pets fontSize="small" />}
              color="primary"
              onClick={() => setDialogType('protocols')}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <IndicatorCard
              title="Tasa de complicaciones"
              value={`${stats.complicationRate}%`}
              subtitle={`${stats.complicationCount} con complicaciones registradas`}
              icon={<Warning fontSize="small" />}
              color="error"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <IndicatorCard
              title="Protocolos completos"
              value={`${stats.documentationRate}%`}
              subtitle={`${stats.fullyDocumented} con registro completo`}
              icon={<Assignment fontSize="small" />}
              color="success"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <IndicatorCard
              title="ASA documentado"
              value={`${stats.asaRate}%`}
              subtitle={`${stats.asaCount} protocolos con ASA`}
              icon={<TrendingUp fontSize="small" />}
              color="info"
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }} variant="outlined">
        <Typography variant="h6" gutterBottom>
          Calidad documental del protocolo
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Box sx={{ mb: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">Con descripcion quirurgica</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {stats.descriptionCount} / {stats.total}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={(stats.descriptionCount / stats.total) * 100}
                color="primary"
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Box sx={{ mb: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">Con hallazgos documentados</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {stats.findingsCount} / {stats.total}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={(stats.findingsCount / stats.total) * 100}
                color="info"
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Box sx={{ mb: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">Con plan postoperatorio</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {stats.postOpPlanCount} / {stats.total}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={(stats.postOpPlanCount / stats.total) * 100}
                color="success"
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Box sx={{ mb: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">Con plan de anestesia</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {stats.planCount} / {stats.total}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={(stats.planCount / stats.total) * 100}
                color="warning"
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Box sx={{ mb: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">Con recuperacion documentada</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {stats.recoveryCount} / {stats.total}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={(stats.recoveryCount / stats.total) * 100}
                color="secondary"
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Box sx={{ mb: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">Con ayuno documentado</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {stats.fastingCount} / {stats.total}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={(stats.fastingCount / stats.total) * 100}
                color="error"
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }} variant="outlined">
        <Typography variant="h6" gutterBottom>
          Cirugías por mes vs año anterior
        </Typography>
        <Box sx={{ width: '100%', height: 300 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 24, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Bar dataKey="previous" name="Año anterior" fill="#2CB8A0" />
              <Bar dataKey="current" name="Año actual" fill="#1A3A5C">
                <LabelList dataKey="current" position="top" content={ChangeLabel} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }} variant="outlined">
        <Typography variant="h6" gutterBottom>
          Cirugías completadas vs protocolos ingresados
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {MESES[month - 1]} {year}: cirugías completadas en la tabla quirúrgica y protocolos asociados encontrados en el sistema.
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <IndicatorCard
              title="Cirugías completadas"
              value={stats.completedTotal}
              subtitle="en la tabla quirúrgica"
              icon={<Pets fontSize="small" />}
              color="primary"
              onClick={() => setDialogType('surgeries')}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <IndicatorCard
              title="Con protocolo de cirujano"
              value={`${stats.completedWithSurgeon}`}
              subtitle={`de ${stats.completedTotal} completadas`}
              icon={<Assignment fontSize="small" />}
              color="success"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <IndicatorCard
              title="Con protocolo de anestesia"
              value={`${stats.completedWithAnesthesia}`}
              subtitle={`de ${stats.completedTotal} completadas`}
              icon={<Assignment fontSize="small" />}
              color="info"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <IndicatorCard
              title="Sin protocolo de cirujano"
              value={stats.completedSurgeonMissing}
              subtitle={`de ${stats.completedTotal} completadas`}
              icon={<Warning fontSize="small" />}
              color="error"
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TopList
              title="Cirujanos con protocolos faltantes"
              data={stats.surgeonsMissing}
              total={stats.completedSurgeonMissing}
              color="error"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TopList
              title="Anestesistas con protocolos faltantes"
              data={stats.anestesiologistsMissing}
              total={stats.completedAnesthesiaMissing}
              color="error"
            />
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <TopList title="Top procedimientos" data={stats.procedureTypes} total={stats.total} color="primary" />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TopList title="Top diagnosticos preoperatorios" data={stats.preOpDiagnoses} total={stats.total} color="info" />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TopList title="Distribucion por especie" data={stats.species} total={stats.total} color="success" />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TopList title="Razas frecuentes" data={stats.breeds} total={stats.total} color="warning" />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TopList title="Cirujanos principales" data={stats.surgeons} total={stats.total} color="secondary" />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TopList title="Protocolos de anestesia por médico" data={stats.anesthesiaByDoctor} total={stats.totalAnesthesia} color="warning" />
        </Grid>
      </Grid>

      <Dialog
        open={!!dialogType}
        onClose={() => setDialogType(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pr: 5 }}>
          {dialogType === 'protocols' ? 'Cirugías registradas (protocolos)' : 'Cirugías completadas (tabla quirúrgica)'}
          <IconButton
            onClick={() => setDialogType(null)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Paciente</TableCell>
                  <TableCell>Procedimiento</TableCell>
                  <TableCell>Cirujano</TableCell>
                  <TableCell>Anestesista</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(dialogType === 'protocols' ? filtered : stats?.completedSurgeries || []).map((item, idx) => (
                  <TableRow key={item.id || idx}>
                    <TableCell>
                      {(() => {
                        const d = getDateFromValue(item.date);
                        return d ? d.toLocaleDateString('es-CL') : '-';
                      })()}
                    </TableCell>
                    <TableCell>{item.patientName || item.patientId || '-'}</TableCell>
                    <TableCell>{item.procedureType || item.procedure || '-'}</TableCell>
                    <TableCell>{item.mainSurgeon || '-'}</TableCell>
                    <TableCell>{item.anesthesiologist || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
